import { createContext, useContext, useState, useRef, useEffect, ReactNode } from "react";
import { PodcastEpisode } from "@/types/podcast";
import { useAuth } from "@/context/AuthContext";
import { podcastListeningService } from "@/services/podcastListeningService";
import { toast } from "sonner";

interface PodcastPlayerContextType {
  currentEpisode: PodcastEpisode | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  playbackRate: number;
  sessionId: string | null;
  playEpisode: (episode: PodcastEpisode) => void;
  pause: () => void;
  resume: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  setPlaybackRate: (rate: number) => void;
  stopPlayback: () => void;
}

const PodcastPlayerContext = createContext<PodcastPlayerContextType | undefined>(undefined);

export const usePodcastPlayer = () => {
  const context = useContext(PodcastPlayerContext);
  if (!context) {
    throw new Error("usePodcastPlayer must be used within PodcastPlayerProvider");
  }
  return context;
};

export const PodcastPlayerProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentEpisode, setCurrentEpisode] = useState<PodcastEpisode | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRateState] = useState(1);
  const [sessionId, setSessionId] = useState<string | null>(null);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastXPMilestone = useRef<number>(0);
  const sessionRef = useRef<string | null>(null);
  const userRef = useRef<typeof user | null>(null);

  // Initialize audio element once
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      // No crossOrigin here: third-party podcast CDNs don't send CORS headers,
      // and requesting them makes the file fail to load (player stuck at 0:00).
      audioRef.current.preload = "metadata";
      audioRef.current.volume = volume;
      audioRef.current.playbackRate = playbackRate;

      const audio = audioRef.current;

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };

      const handleError = () => {
        console.error('Podcast audio error', audio.error?.code, audio.error?.message, audio.currentSrc);
        setIsPlaying(false);
      };

      const handleDurationChange = () => {
        setDuration(audio.duration);
      };

      const handleLoadedMetadata = () => {
        console.log('Metadata loaded, duration:', audio.duration);
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          setDuration(audio.duration);
        }
      };

      const handleEnded = () => {
        setIsPlaying(false);
        const currentSessionId = sessionRef.current;
        const currentUser = userRef.current;
        if (currentSessionId && currentUser) {
          podcastListeningService.endSession(currentSessionId, audio.currentTime).catch(console.error);
          podcastListeningService.awardXPForListening(currentSessionId, currentUser.id).then((result) => {
            if (result.xpEarned > 0) {
              toast.success(`🎉 Episode complete! You earned ${result.xpEarned} XP!`);
            }
          }).catch(console.error);
        }
        setSessionId(null);
        lastXPMilestone.current = 0;
      };

      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('durationchange', handleDurationChange);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      audio.addEventListener('playing', () => setIsPlaying(true));
      audio.addEventListener('pause', () => setIsPlaying(false));

      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('durationchange', handleDurationChange);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
      };
    }
  }, []);

  // Keep sessionRef in sync
  useEffect(() => {
    sessionRef.current = sessionId;
  }, [sessionId]);

  // Keep userRef in sync
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Sync volume with audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Sync playback rate with audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Sync mute state with audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Start listening session when playing
  useEffect(() => {
    if (isPlaying && duration > 0 && isAuthenticated && user && currentEpisode && !sessionId) {
      podcastListeningService
        .startListeningSession(user.id, currentEpisode.id, currentEpisode.podcast_id, duration)
        .then((session) => {
          setSessionId(session.id);
          // Log play event
          podcastListeningService.logPlaybackEvent(
            session.id,
            user.id,
            currentEpisode.id,
            'play',
            audioRef.current?.currentTime || 0
          ).catch(console.error);
        })
        .catch(console.error);
    }
  }, [isPlaying, duration, isAuthenticated, user, currentEpisode, sessionId]);

  // Update progress every 30 seconds
  useEffect(() => {
    if (isPlaying && sessionId && audioRef.current) {
      progressIntervalRef.current = setInterval(() => {
        if (audioRef.current && duration > 0) {
          const progress = audioRef.current.currentTime;
          podcastListeningService.updateProgress(sessionId, progress).catch(console.error);
          
          const completionPercentage = Math.floor((progress / duration) * 100);
          const milestones = [25, 50, 75, 100];
          
          for (const milestone of milestones) {
            if (completionPercentage >= milestone && lastXPMilestone.current < milestone) {
              lastXPMilestone.current = milestone;
              if (user) {
                podcastListeningService.awardXPForListening(sessionId, user.id).then((result) => {
                  if (result.xpEarned > 0) {
                    toast.success(`🎉 You earned ${result.xpEarned} XP!`, {
                      description: `${result.completionPercentage}% complete`
                    });
                  }
                }).catch(console.error);
              }
              break;
            }
          }
        }
      }, 30000);

      return () => {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      };
    }
  }, [isPlaying, sessionId, duration, user]);

  const playEpisode = (episode: PodcastEpisode) => {
    if (!audioRef.current) return;

    // If same episode, just resume
    if (currentEpisode?.id === episode.id) {
      resume();
      return;
    }

    // Stop current playback
    if (sessionId && audioRef.current && user) {
      const finalProgress = audioRef.current.currentTime;
      podcastListeningService.endSession(sessionId, finalProgress).catch(console.error);
    }

    // Load new episode
    setCurrentEpisode(episode);
    setSessionId(null);
    lastXPMilestone.current = 0;
    
    // Set initial duration from episode metadata if available
    if (episode.duration_seconds) {
      setDuration(episode.duration_seconds);
    }
    
    audioRef.current.src = episode.audio_url;
    audioRef.current.load();
    
    // Play when ready
    audioRef.current.play().then(() => {
      setIsPlaying(true);
    }).catch(console.error);
  };

  const pause = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
    
    // Log pause event
    if (sessionId && user && currentEpisode) {
      podcastListeningService.logPlaybackEvent(
        sessionId,
        user.id,
        currentEpisode.id,
        'pause',
        audioRef.current.currentTime
      ).catch(console.error);
    }
  };

  const resume = () => {
    if (!audioRef.current) return;
    audioRef.current.play().then(() => {
      setIsPlaying(true);
      
      // Log play event
      if (sessionId && user && currentEpisode) {
        podcastListeningService.logPlaybackEvent(
          sessionId,
          user.id,
          currentEpisode.id,
          'play',
          audioRef.current!.currentTime
        ).catch(console.error);
      }
    }).catch(console.error);
  };

  const seek = (time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
    
    // Log seek event
    if (sessionId && user && currentEpisode) {
      podcastListeningService.logPlaybackEvent(
        sessionId,
        user.id,
        currentEpisode.id,
        'seek',
        time
      ).catch(console.error);
    }
  };

  const setVolume = (newVolume: number) => {
    if (!audioRef.current) return;
    setVolumeState(newVolume);
    
    // Log volume change event
    if (sessionId && user && currentEpisode) {
      podcastListeningService.logPlaybackEvent(
        sessionId,
        user.id,
        currentEpisode.id,
        'volume_change',
        audioRef.current.currentTime,
        { volume: newVolume }
      ).catch(console.error);
    }
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    const nextMuted = !audioRef.current.muted;
    audioRef.current.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  const setPlaybackRate = (rate: number) => {
    if (!audioRef.current) return;
    setPlaybackRateState(rate);
    
    // Log speed change event
    if (sessionId && user && currentEpisode) {
      podcastListeningService.logPlaybackEvent(
        sessionId,
        user.id,
        currentEpisode.id,
        'speed_change',
        audioRef.current.currentTime,
        { playbackRate: rate }
      ).catch(console.error);
    }
  };

  const stopPlayback = () => {
    if (!audioRef.current) return;
    
    // End session
    if (sessionId && user) {
      const finalProgress = audioRef.current.currentTime;
      podcastListeningService.endSession(sessionId, finalProgress).catch(console.error);
    }
    
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentEpisode(null);
    setSessionId(null);
    setCurrentTime(0);
    lastXPMilestone.current = 0;
  };

  return (
    <PodcastPlayerContext.Provider
      value={{
        currentEpisode,
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        playbackRate,
        sessionId,
        playEpisode,
        pause,
        resume,
        seek,
        setVolume,
        toggleMute,
        setPlaybackRate,
        stopPlayback,
      }}
    >
      {children}
    </PodcastPlayerContext.Provider>
  );
};
